import React, { useEffect, useState, ChangeEvent } from 'react';
import {
    DefaultButton,
    PrimaryButton,
    MessageBar,
    MessageBarType,
    Separator,
    Panel,
    PanelType,
} from '@fluentui/react';
import { Combobox, Field, Input, InputProps, Spinner, Textarea, TextareaProps } from '@fluentui/react-components';
import { Option } from '@fluentui/react-components';
import { useTranslation, Trans } from 'react-i18next';
import { connect } from 'react-redux';
import { ErrorMessageComponent } from '../../../Error/ErrorMessageComponent';
import * as ComponentFunctions from './StageTypeForm.Functions';
import { ContractStageType } from '../../../../models/Entities/Contracts/ContractStageType';
import type { ComboboxProps } from "@fluentui/react-components";

interface FormValidation {
    success: boolean;
    errors: string[];
}

export const ContractStageTypeFormComponent = (props: ComponentFunctions.Props) => {
    const [submittedForm, setSubmittedForm] = useState<boolean>(false);
    const [FormValidationStatus, setFormValidationStatus] = useState<FormValidation | undefined>();
    const [name, setName] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [relatedWords, setRelatedWords] = useState<string>('');
    const [type, setType] = useState<number>(0);
    const [priceCategory, setPriceCategory] = useState<number>(0);
    const [t] = useTranslation();
    const [selectedType, setSelectedType] = useState<string>('');

    useEffect(() => {
        if (props.StageTypeId) props.GetContractStageTypeById(props.StageTypeId);
    }, [props.StageTypeId]);

    useEffect(() => {
        if (submittedForm && FormValidationStatus?.success && props.isSavedSuccessfully) {
            ResetForm();
            props.dismissPanel();
        }
    }, [submittedForm, props.isSavedSuccessfully]);

    const options = [
        { value: "0", text: t('stageTypeForm.props.typeProps.screening') },
        { value: "1", text: t('stageTypeForm.props.typeProps.random') },
        { value: "2", text: t('stageTypeForm.props.typeProps.treatment') },
        { value: "3", text: t('stageTypeForm.props.typeProps.followUp') },
        { value: "4", text: t('stageTypeForm.props.typeProps.preScreening') }
    ];
    useEffect(() => {
        if (props.StageType?.id) {
            setName(props.StageType.name);
            setDescription(props.StageType.description ?? '');
            setRelatedWords(props.StageType.relatedWords ?? '');
            setType(props.StageType.type ?? 0);
            setPriceCategory(props.StageType.priceCategory ?? 0);

            const selectedOption = options.find(option => option.value === String(props.StageType?.type ?? 0))?.text ?? '';
            setSelectedType(selectedOption);
        } else {
            setName('');
            setDescription('');
            setRelatedWords('');
            setType(0);
            setPriceCategory(0);
            setSelectedType('');
        }
    }, [props.StageTypeId, props.StageType?.id, t]);

    const ResetForm = () => {
        setSubmittedForm(false);
        setName('');
        setDescription('');
        setRelatedWords('');
        setType(0);
        setPriceCategory(0);
        setSelectedType('');
    };

    const onChangeName: InputProps['onChange'] = (event, data) => {
        setName(data?.value || '');
    };
    const onChangeDescription: TextareaProps['onChange'] = (event, data) => {
        setDescription(data?.value || '');
    };
    const onChangeRelatedWords: InputProps['onChange'] = (event, data) => {
        setRelatedWords(data?.value || '');
    };
    const onChangeType: InputProps['onChange'] = (event, data) => {
        setType(Number(data?.value) || 0);
    };
    const onChangePriceCategory: InputProps['onChange'] = (event, data) => {
        setPriceCategory(Number(data?.value) || 0);
    };

    const ValidateForm = (): boolean => {
        const errors: string[] = [];
        if (!name) {
            errors.push(t('stageTypeForm.props.requiredName'));
        }
        if (errors.length > 0) {
            setFormValidationStatus({ success: false, errors });
            return false;
        }
        setFormValidationStatus({ success: true, errors: [] });
        return true;
    }

    const HandleSaveForm = () => {
        setSubmittedForm(true);
        if (ValidateForm()) {
            const values: Partial<ContractStageType> = {
                id: props.StageType?.id,
                name,
                description,
                relatedWords,
                type,
                priceCategory
            };
            if (props.StageType) {
                props.UpdateContractStageType(props.StageType.id, values);
            } else {
                props.CreateContractStageType(values);
            }
        }
    };

    const HandleCancelForm = () => {
        ResetForm();
        props.dismissPanel();
    };

    const onRenderFooterContent = () => (
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <DefaultButton disabled={props.isSaving} onClick={HandleCancelForm}>
                <Trans i18nKey={'stageTypeForm.footer.cancelButton'} />
            </DefaultButton>
            <PrimaryButton onClick={HandleSaveForm} disabled={props.isSaving}>
                {props.isSaving && <><Spinner size="extra-tiny" />&nbsp;</>}
                <Trans i18nKey={'stageTypeForm.footer.saveButton'} />
            </PrimaryButton>
        </div>
    );


    const formFields = (
        <div>
            <Field required label={t('stageTypeForm.props.name')}
                validationState={!submittedForm ? 'none' : !name ? 'error' : 'success'}
                validationMessage={submittedForm && !name ? t('stageTypeForm.props.requiredName') : ''}
            >
                <Input value={name} onChange={onChangeName} />
            </Field>
            <Field label={t('stageTypeForm.props.description')}>
                <Textarea value={description} onChange={onChangeDescription} />
            </Field>
            <Field label={t('stageTypeForm.props.relatedWords')}>
                <Input value={relatedWords} onChange={onChangeRelatedWords} />
            </Field>
            <Field label={t('stageTypeForm.props.typeLabel')}>
                <Combobox
                    aria-labelledby={"stageType"}
                    placeholder={t('stageTypeForm.props.type.placeholder')}
                    value={selectedType}
                    onOptionSelect={(_, data) => {
                        const selectedOption = options?.find(option => option.text === data.optionValue);
                        if (selectedOption) {
                            setType(Number(selectedOption.value));
                            setSelectedType(selectedOption.text);
                        } else {
                            setType(0);
                            setSelectedType('');
                        }
                    }}
                >
                    {options.map(option => (
                        <Option key={option.value} value={option.text} >
                            {option.text}
                        </Option>
                    ))}
                </Combobox>
            </Field>
        </div>
    );

    return (
        <Panel
            isOpen={props.isOpen}
            onDismiss={props.dismissPanel}
            type={PanelType.medium}
            headerText={props.StageType ? t('stageTypeForm.title.edit') : t('stageTypeForm.title.create')}
            onRenderFooterContent={onRenderFooterContent}
            isFooterAtBottom={true}
        >
            {props.error && (
                <ErrorMessageComponent Error={props.error} />
            )}
            {formFields}
        </Panel>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(ContractStageTypeFormComponent as any);


